import React from 'react';
import { Inspection, InspectionStatus, InspectionType } from '../types';
import { TablePagination, useTablePagination } from '../../../../shared/TablePagination';
import { useTableSort, SortableTh } from '../../../../shared/useTableSort';

interface InspectionListTableProps {
  inspections: Inspection[];
  loading: boolean;
  filterStatus: 'ALL' | InspectionStatus;
  filterType: 'ALL' | InspectionType;
  onFilterStatusChange: (status: 'ALL' | InspectionStatus) => void;
  onFilterTypeChange: (type: 'ALL' | InspectionType) => void;
  onSelectInspection: (inspection: Inspection) => void;
  onOpenCreate: () => void;
}

export const InspectionListTable: React.FC<InspectionListTableProps> = ({
  inspections,
  loading,
  filterStatus,
  filterType,
  onFilterStatusChange,
  onFilterTypeChange,
  onSelectInspection,
  onOpenCreate,
}) => {
  const filtered = inspections.filter((item) => {
    if (filterStatus !== 'ALL' && item.status !== filterStatus) return false;
    if (filterType !== 'ALL' && item.inspectionType !== filterType) return false;
    return true;
  });

  const {
    sortedItems,
    sortField,
    sortDirection,
    requestSort,
  } = useTableSort(filtered);

  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalItems,
    paginatedItems,
  } = useTablePagination(sortedItems, 25);

  const getStatusBadge = (status: InspectionStatus) => {
    switch (status) {
      case 'DRAFT':
        return <span className="badge-pill badge-neutral">Draft</span>;
      case 'PLANNED':
      case 'SCHEDULED':
      case 'ASSIGNED':
        return <span className="badge-pill badge-info">Scheduled</span>;
      case 'IN_PROGRESS':
        return <span className="badge-pill badge-warning">Field In Progress</span>;
      case 'FIELD_CHECK_COMPLETED':
      case 'REPORT_DRAFT':
        return <span className="badge-pill badge-info">Report In Review</span>;
      case 'REPORT_APPROVED':
        return <span className="badge-pill badge-success">Approved</span>;
      case 'CLOSED':
        return <span className="badge-pill badge-success">Closed</span>;
      case 'CANCELLED':
        return <span className="badge-pill badge-danger">Cancelled</span>;
      default:
        return <span className="badge-pill badge-neutral">{status}</span>;
    }
  };

  const getTypeBadge = (type: InspectionType) => {
    switch (type) {
      case 'RAMP':
        return <span className="badge-pill badge-info">Ramp Turnaround</span>;
      case 'AIRCRAFT_PHYSICAL':
        return <span className="badge-pill badge-info">Aircraft Physical</span>;
      case 'CABIN_SAFETY':
        return <span className="badge-pill badge-neutral">Cabin Safety</span>;
      case 'TOOLING':
        return <span className="badge-pill badge-warning">Tooling / Equipment</span>;
      case 'HANGAR_FACILITY':
        return <span className="badge-pill badge-neutral">Hangar & Facility</span>;
      case 'DANGEROUS_GOODS':
        return <span className="badge-pill badge-danger">Dangerous Goods</span>;
      case 'AD_HOC':
        return <span className="badge-pill badge-neutral">Ad-Hoc</span>;
      default:
        return <span className="badge-pill badge-neutral">{type}</span>;
    }
  };

  const getResultBadge = (result?: string) => {
    if (!result) return null;
    switch (result) {
      case 'SATISFACTORY':
        return <span className="badge-pill badge-success">SATISFACTORY</span>;
      case 'ACCEPTABLE':
        return <span className="badge-pill badge-info">ACCEPTABLE</span>;
      case 'UNSATISFACTORY':
        return <span className="badge-pill badge-warning">UNSATISFACTORY</span>;
      case 'CRITICAL_FAIL':
        return <span className="badge-pill badge-danger">CRITICAL FAIL</span>;
      default:
        return <span className="badge-pill badge-neutral">{result}</span>;
    }
  };

  return (
    <div className="card" style={{ padding: '0', overflow: 'hidden' }}>
      {/* Controls Bar */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '16px', background: '#f8fafc' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontSize: '12px', fontWeight: 600, color: '#64748b' }}>Status:</label>
            <select
              value={filterStatus}
              onChange={(e) => onFilterStatusChange(e.target.value as any)}
              style={{ fontSize: '13px', border: '1px solid #cbd5e1', borderRadius: '6px', padding: '6px 10px', background: '#ffffff', color: '#1e293b' }}
            >
              <option value="ALL">All Statuses ({inspections.length})</option>
              <option value="SCHEDULED">Scheduled</option>
              <option value="IN_PROGRESS">Field In Progress</option>
              <option value="FIELD_CHECK_COMPLETED">Field Completed</option>
              <option value="REPORT_DRAFT">Report Draft</option>
              <option value="REPORT_APPROVED">Report Approved</option>
              <option value="CLOSED">Closed</option>
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontSize: '12px', fontWeight: 600, color: '#64748b' }}>Type:</label>
            <select
              value={filterType}
              onChange={(e) => onFilterTypeChange(e.target.value as any)}
              style={{ fontSize: '13px', border: '1px solid #cbd5e1', borderRadius: '6px', padding: '6px 10px', background: '#ffffff', color: '#1e293b' }}
            >
              <option value="ALL">All Types</option>
              <option value="RAMP">Ramp Turnaround</option>
              <option value="AIRCRAFT_PHYSICAL">Aircraft Physical</option>
              <option value="CABIN_SAFETY">Cabin Safety</option>
              <option value="TOOLING">Tooling & Calibration</option>
              <option value="HANGAR_FACILITY">Hangar & Facility</option>
              <option value="DANGEROUS_GOODS">Dangerous Goods</option>
              <option value="AD_HOC">Special / Ad-Hoc</option>
            </select>
          </div>
        </div>

        <button
          onClick={onOpenCreate}
          className="btn-primary"
          style={{ width: 'auto', padding: '8px 16px', fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
        >
          <span>+ New Inspection</span>
        </button>
      </div>

      {/* Table Content */}
      <div className="table-container" style={{ margin: '0', border: 'none', borderRadius: '0' }}>
        <table className="data-table">
          <thead>
            <tr>
              <SortableTh field="inspectionNumber" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Inspection No
              </SortableTh>
              <SortableTh field="inspectionType" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Category
              </SortableTh>
              <SortableTh field="title" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Title & Asset / Flight
              </SortableTh>
              <SortableTh field="department" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Department & Location
              </SortableTh>
              <SortableTh field="leadInspectorName" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Lead Inspector
              </SortableTh>
              <SortableTh field="scheduledDate" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Date
              </SortableTh>
              <SortableTh field="status" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Status / Result
              </SortableTh>
              <th style={{ textAlign: 'center' }}>Findings</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!loading && filtered.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ textAlign: 'center', padding: '32px', color: '#64748b' }}>
                  No inspections match the selected criteria.
                </td>
              </tr>
            ) : (
              paginatedItems.map((item) => (
                <tr key={item.id} style={{ cursor: 'pointer' }} onClick={() => onSelectInspection(item)}>
                  <td style={{ fontFamily: 'monospace', fontWeight: 600, color: '#0284c7' }}>
                    {item.inspectionNumber}
                  </td>
                  <td>
                    {getTypeBadge(item.inspectionType)}
                  </td>
                  <td>
                    <div style={{ fontWeight: 600, color: '#0f172a' }}>{item.title}</div>
                    <div style={{ fontSize: '12px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                      {item.assetReference && (
                        <span style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px', fontFamily: 'monospace', fontSize: '11px', color: '#334155' }}>
                          ✈ {item.assetReference}
                        </span>
                      )}
                      {item.flightNumber && (
                        <span style={{ background: '#e0f2fe', color: '#0369a1', padding: '2px 6px', borderRadius: '4px', fontFamily: 'monospace', fontSize: '11px' }}>
                          FLT: {item.flightNumber}
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div style={{ fontWeight: 500, color: '#1e293b' }}>{item.department}</div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>{item.location}</div>
                  </td>
                  <td style={{ fontWeight: 500, color: '#334155' }}>
                    {item.leadInspectorName}
                  </td>
                  <td style={{ fontFamily: 'monospace', fontSize: '12px', color: '#475569' }}>
                    {item.scheduledDate}
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                      {getStatusBadge(item.status)}
                      {item.criticalContainmentApplied && (
                        <span className="badge-pill badge-danger" style={{ animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }}>
                          ⚠ STOP-WORK / AOG
                        </span>
                      )}
                      {getResultBadge(item.overallResult)}
                    </div>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {item.findingCount > 0 ? (
                      <span className="badge-pill badge-warning">
                        {item.findingCount}
                      </span>
                    ) : (
                      <span style={{ fontSize: '12px', color: '#94a3b8' }}>-</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectInspection(item);
                      }}
                      className="btn-secondary"
                      style={{ width: 'auto', padding: '6px 12px', fontSize: '12px' }}
                    >
                      Review →
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <TablePagination
        currentPage={currentPage}
        totalItems={totalItems}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        itemLabel="inspections"
      />
    </div>
  );
};
